import React, { Fragment, FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { getSortedTimeZoneLabels } from 'shared/timeZoneUtils/timeZone.utils';
import { FilterModel } from 'shared/commonGrid/common-grid.types';
import { adaptToServerValueFilterModel } from 'shared/commonGrid/filterModel.adapter';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import { TimeZoneCellRenderer } from 'shared/commonGrid/TimeZoneCellRenderer';
import ModalDialog from 'shared/modal/dialog';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { Template, TEMPLATE_CATEGORY_WARRANT_WIZARD } from 'data/template/template.types';
import MissingTemplateDialogBody from 'routes/Warrant/components/MissingTemplateDialogBody';
import cloneDeep from 'lodash.clonedeep';
import { showError } from 'shared/toaster';
import ExportReportDialogBody, { ExportReportDialogBodyFormRef } from 'routes/AccessFunction/containers/AccessFunctionList/components/DialogBody/ExportReportDialogBody';
import moment from 'moment';
import CaseGridColumnDefs from './case-grid-column-defs';
import CommonGrid, { ICommonGridProperties } from '../../../shared/commonGrid';
import DateTimeCellRenderer from '../../../shared/commonGrid/date-cell-renderer';
import { COMMON_GRID_ACTION, ROWSELECTION } from '../../../shared/commonGrid/grid-enums';
import DateTimeWithTimezoneCellRenderer from '../../../shared/commonGrid/DateTimeWithTimezoneCellRenderer';
import ActionMenuBar from '../actions-bar/action-menu';
import * as caseActions from '../case-actions';
import { exportCasesToFile } from './utils';

type CaseGridState = {
  selection: any[],
  selectionLastCleared: Date | null,
  confirmActionDialog: {
    isOpen: boolean
    title: string
    customComponent: FunctionComponent
    onSubmit: CallableFunction
    actionText: string
  },
  exportReportDialog: {
    isOpen: boolean
    title: string
    customComponent: FunctionComponent
    onSubmit: CallableFunction
    actionText: string
    onClose: CallableFunction
  },
}

class CaseGrid extends React.Component<any, CaseGridState> {
  CASE_GRID_SETTINGS = 'caseGridSettings';
  commonGridRef: any;
  exportReportDialogRef: ExportReportDialogBodyFormRef | null = null;
  columnDefs = CaseGridColumnDefs;
  filterCriteria = '';

  state: CaseGridState = {
    selection: [],
    selectionLastCleared: null,
    confirmActionDialog: {
      isOpen: false,
      title: '',
      customComponent: () => null,
      onSubmit: () => null,
      actionText: ''
    },
    exportReportDialog: {
      isOpen: false,
      title: '',
      customComponent: () => null,
      onSubmit: () => null,
      actionText: '',
      onClose: () => null,
    }
  };

  initializeColumnDef = () => {
    const { timezones } = this.props;

    const timeZoneCol = this.columnDefs.find(
      (columnDef) => columnDef.field === 'timeZone'
    );
    if (timeZoneCol) {
      timeZoneCol.filter = 'agSetColumnFilter';
      timeZoneCol.filterParams = {
        values: getSortedTimeZoneLabels(timezones)
      };
    }
  };

  isWarrantWizardTemplateAvailable(): boolean {
    const { templates } = this.props;
    const filteredTemplates =
      templates?.filter(
        ({ category }: Template) =>
          category === TEMPLATE_CATEGORY_WARRANT_WIZARD
      ) ?? [];
    return filteredTemplates.length > 0;
  }

  showPopupForUnavailableTemplate(title: string): void {
    const confirmActionDialog = {
      isOpen: true,
      title: i18n.t('warrant.grid.missingTemplate.title', { title }),
      customComponent: () => <MissingTemplateDialogBody />,
      actionText: i18n.t('common.button.close'),
      onSubmit: () => {
        confirmActionDialog.isOpen = false;
        this.setState({ confirmActionDialog });
      }
    };

    this.setState({ confirmActionDialog });
  }

  handleSelection = (e: any) => {
    this.setState({ selection: e.api.getSelectedRows() });
    this.props.broadcastSelectedCases(e.api.getSelectedRows());
  };

  handleGetResultsFromServer = (body: any) => {
    this.filterCriteria = cloneDeep(body);
    return this.props.getAllCases(0, body);
  };

  handleGetGridSettingsFromServer = () => {
    return this.props.getUserSettings(
      this.CASE_GRID_SETTINGS,
      this.props.authentication.userId
    );
  };

  saveGridSettings = (caseGridSettings: { [key: string]: any }) => {
    if (caseGridSettings !== null) {
      this.props.putUserSettings(
        this.CASE_GRID_SETTINGS,
        this.props.authentication.userId,
        caseGridSettings
      );
    }
  };

  reloadGridDataFromServer = () => {
    if (this.commonGridRef) {
      this.commonGridRef.handleGridReloadFromServer(
        this.handleGetResultsFromServer,
        null
      );
    }
  };

  onActionMenuItemClick = (e: any) => {
    const { type } = e;
    switch (type) {
      case COMMON_GRID_ACTION.Edit:
        this.handleEditWarrantWizard();
        break;
      case COMMON_GRID_ACTION.View:
        this.handleViewWarrantWizard();
        break;
      default:
    }
  };

  onHandleExport = () => {
    const exportReportDialog = {
      isOpen: true,
      title: i18n.t('case.exportPopup.action'),
      customComponent: () => (
        <ExportReportDialogBody
          ref={(ref: any) => {
            this.exportReportDialogRef = ref;
          }}
          exportOption={{
            supportedFileFormats: ['csv'],
            fileFormat: 'csv',
            filename: `cases_export_${moment().valueOf()}`,
            isExporting: false
          }}
        >
          {i18n.t('case.exportPopup.message')}
        </ExportReportDialogBody>
      ),
      actionText: i18n.t('case.exportPopup.action'),
      onSubmit: this.onReportSubmit,
      onClose: () => {
        this.setState({
          exportReportDialog: {
            ...this.state.exportReportDialog,
            isOpen: false
          }
        });
      }
    };

    this.setState({ exportReportDialog });
  };

  onReportSubmit = () => {
    if (this.exportReportDialogRef) {
      exportCasesToFile(this.filterCriteria, this.exportReportDialogRef.getExportOption()).then((isSuccessful) => {
        if (!isSuccessful) {
          showError('Failed to export cases to file.');
        }
      });
    }
  };

  handleEditWarrantWizard = () => {
    if (!this.isWarrantWizardTemplateAvailable()) {
      this.showPopupForUnavailableTemplate(i18n.t('warrant.grid.edit'));
      return;
    }

    const {
      history,
      goToWarrantEdit,
      broadcastSelectedWarrants,
      getWarrantById,
      clearCasesState,
      broadcastSelectedWarrantCase
    } = this.props;
    const { selectedDF: dfId } = this.props.global;
    const { warrantId } = this.state.selection[0];
    getWarrantById(dfId, warrantId.id).then(() => {
      clearCasesState();
      broadcastSelectedWarrants([this.props.warrant]);
      broadcastSelectedWarrantCase(this.state.selection[0]);
      goToWarrantEdit(history);
    });
  };

  handleViewWarrantWizard = async () => {
    if (!this.isWarrantWizardTemplateAvailable()) {
      this.showPopupForUnavailableTemplate(i18n.t('warrant.grid.view'));
      return;
    }

    const {
      history,
      goToWarrantView,
      broadcastSelectedWarrants,
      getWarrantById,
      clearCasesState,
      broadcastSelectedWarrantCase
    } = this.props;
    const { selectedDF: dfId } = this.props.global;
    const { warrantId } = this.state.selection[0];
    getWarrantById(dfId, warrantId.id).then(() => {
      clearCasesState();
      broadcastSelectedWarrants([this.props.warrant]);
      broadcastSelectedWarrantCase(this.state.selection[0]);
      goToWarrantView(history);
    });
  };

  componentDidMount() {
    const { clearSelectedCases } = this.props;

    clearSelectedCases();
  }

  componentDidUpdate() {
    this.initializeColumnDef();
    if (this.props.selectionLastCleared !== this.state.selectionLastCleared) {
      this.setState(
        { selectionLastCleared: this.props.selectionLastCleared },
        () => {
          this.reloadGridDataFromServer();
        }
      );
    }
  }

  componentWillUnmount(): void {
    const { clearCasesState } = this.props;

    clearCasesState();
  }

  adaptToGridFilterModel = (filterModel: FilterModel): FilterModel => {
    return adaptToServerValueFilterModel(
      filterModel,
      'timeZone',
      this.props.timezones
    );
  };

  render() {
    const { confirmActionDialog, exportReportDialog } = this.state;

    const components = {
      datetimeCellRenderer: DateTimeCellRenderer,
      DateTimeWithTimezoneCellRenderer,
      TimeZoneCellRenderer
    };
    const commonGridProps: ICommonGridProperties = {
      onActionMenuItemClick: this.onActionMenuItemClick,
      onSelectionChanged: this.handleSelection,
      rowSelection: ROWSELECTION.MULTIPLE,
      columnDefs: this.columnDefs,
      rowData: this.props.cases.cases,
      enableBrowserTooltips: true,
      context: this,
      pagination: true,
      noResults: false,
      components,
      getGridResultsFromServer: this.handleGetResultsFromServer,
      adaptToFilterModel: this.adaptToGridFilterModel,
      renderDataFromServer: true,
      getGridSettingsFromServer: this.handleGetGridSettingsFromServer,
      saveGridSettings: this.saveGridSettings,
      isBeforeLogout: this.props.authentication.beforeLogout,
      onExport: this.onHandleExport
    };
    return (
      <Fragment>
        <div className="wiretap-grid-container" style={{ height: 'inherit' }}>
          <CommonGrid
            data-test="warrants-common-grid"
            ref={(ref: any) => {
              this.commonGridRef = ref;
            }}
            {...commonGridProps}
          >
            <ActionMenuBar handleEdit={this.handleEditWarrantWizard} />
          </CommonGrid>
        </div>
        <ModalDialog
          width="500px"
          isOpen={confirmActionDialog.isOpen}
          title={confirmActionDialog.title}
          customComponent={confirmActionDialog.customComponent}
          actionText={confirmActionDialog.actionText}
          onSubmit={confirmActionDialog.onSubmit}
          doNotDisplayCancelButton
        />
        <ModalDialog
          width="500px"
          isOpen={exportReportDialog.isOpen}
          title={exportReportDialog.title}
          customComponent={exportReportDialog.customComponent}
          actionText={exportReportDialog.actionText}
          onSubmit={exportReportDialog.onSubmit}
          onClose={exportReportDialog.onClose}
        />
      </Fragment>
    );
  }
}

const mapStateToProps = ({
  discoveryXcipio: {
    discoveryXcipioSettings: {
      isBulkActionsEnabled,
    },
  },
  warrants: { warrant, templates },
  cases: { selectionLastCleared, selectedCases },
  users: {
    timezones,
  },
}: RootState) => ({
  isBulkActionsEnabled,
  selectionLastCleared,
  selectedCases,
  timezones,
  warrant,
  templates,
});

const mapDispatchToProps = {
  broadcastSelectedCases: caseActions.broadcastSelectedCases,
  clearCasesState: caseActions.clearCasesState,
  clearSelectedCases: caseActions.clearSelectedCases,
  goToWarrantEdit: warrantActions.goToWarrantEdit,
  goToWarrantView: warrantActions.goToWarrantView,
  broadcastSelectedWarrants: warrantActions.broadcastSelectedWarrants,
  getWarrantById: warrantActions.getWarrantById,
  broadcastSelectedWarrantCase: warrantActions.broadcastSelectedWarrantCase,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(CaseGrid);
